import _ from "lodash";
/** external */
import React, { useState, memo, Fragment, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  AddOutlined,
  AddCircleOutlined,
  PageSquareOutlined,
} from "@toddle-edu/ds-icons";
import { Dropdown, IconButton, Button } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { connect } from "react-redux";
/** internal */
import { useI18n } from "Hooks";
import { getQuestionDropdownOptionsV2 } from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  getIsWorksheetAIEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  WORKSHEET_ELEMENT_IDS,
  ADD_QUESTION_FROM_LIBRARY,
  EXTRACT_FROM_DOC,
  GENERATE_USING_AI,
} from "Worksheet/modules/WorksheetConstants";
import QuestionLibraryModal from "Worksheet/components/QuestionLibraryModal";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { updateAddQuestionsInBetween } from "Worksheet/modules/WorksheetModules";

/** relative */
import classes from "./AddQuestionsDropdownV2.scss";

const AddQuestionsDropdownV2 = memo(props => {
  const {
    isSubQuestion,
    onOptionClick,
    parentResourceId, // last section's resource id
    putAfterItemId,
    addSection,
    showAddInstructions,
    worksheetDetailsRef,
    setToastMsg,
    updateActiveQuestionId,
    handleAiQuestionGenerator,
    labelLocale,
    isButtonRounded,
    curriculumType,
    isFullWidthBtn,
    isLargeButton,
    isGeneratingAIQuestions,
    isFromInternalTool,
    isToddleLibraryPortal,
    isRevampedSinglePageUI,
    updateWorksheetSidebar,
    btnComponent,
    showAddSection,
    isAddDropdownButtonRevamp,
    updateAddQuestionsInBetween,
    showAddSeparator,
    showAddInlineText,
    isAddingQuestionsInBetween,
    sectionResourceId, // current section's resouce id for adding questions in between
    questionResourceId,
    setAddButtonDropdownActiveId,
    isAddingAtStartOfMultipart,
    isAIAssessmentToolsOpen,
    isAIQuestionAssistantOpen,
    questionId,
    parentItemId, // last section's item id
    sectionItemId, // current section's item id for adding questions in between
    isQueriesV2Enabled,
    activeQuestionId,
  } = props;
  const { t } = useI18n(["common", "worksheet"]);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [focusableOptions, setFocusableOptions] = useState([]);
  const [activeFocusOptionIndex, setActiveFocusOptionIndex] = useState(-1);
  const [showQuestionLibraryModal, setShowQuestionLibraryModal] = useState(
    false
  );
  const dropdownRef = useRef();

  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  const isWorksheetAIEnabled = getIsWorksheetAIEnabled();

  useEffect(() => {
    setShowQuestionLibraryModal(activeQuestionId === ADD_QUESTION_FROM_LIBRARY);
  }, [activeQuestionId]);

  // Utility to get all focusable leaf nodes
  function getFocusableLeafNodes(parent) {
    const focusableSelectors =
      'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

    // Get all focusable elements within the parent
    const allFocusableElements = parent.querySelectorAll(focusableSelectors);

    // Filter leaf nodes (elements without other focusable children)
    const leafNodes = Array.from(allFocusableElements).filter(element => {
      return element.querySelector(focusableSelectors) === null;
    });

    return leafNodes;
  }

  useEffect(() => {
    if (isAddDropdownButtonRevamp) return;
    if (!isDropdownVisible) {
      setActiveFocusOptionIndex(-1);
      setFocusableOptions([]);
      dropdownRef.current = null;
      return;
    }
    if (dropdownRef?.current) {
      const leafNodes = getFocusableLeafNodes(dropdownRef.current);
      setFocusableOptions(leafNodes); // Store leaf nodes in state
      if (leafNodes.length > 0) setActiveFocusOptionIndex(0);
    }
    if (focusableOptions.length > 0) {
      setActiveFocusOptionIndex(0);
    }
  }, [isDropdownVisible, isAddDropdownButtonRevamp]);

  useEffect(() => {
    setActiveFocusOptionIndex(-1);
    setTimeout(() => {
      if (dropdownRef?.current) {
        const leafNodes = getFocusableLeafNodes(dropdownRef.current);
        setFocusableOptions(leafNodes); // Store leaf nodes in state

        if (leafNodes.length > 0) setActiveFocusOptionIndex(0);
      }
    }, 100);
  }, [dropdownRef.current]);

  // Focus the active option whenever the index changes
  useEffect(() => {
    if (
      focusableOptions.length > 0 &&
      activeFocusOptionIndex !== null &&
      focusableOptions[activeFocusOptionIndex]
    ) {
      focusableOptions[activeFocusOptionIndex].focus();
    }
  }, [activeFocusOptionIndex]);

  const {
    groupedOptions,
    contentOption = {},
    separatorOption = {},
  } = getQuestionDropdownOptionsV2({
    t,
    isSubQuestion,
    curriculumType,
    isFromInternalTool,
  });

  const isValid = () => {
    const _isValid = _.get(worksheetDetailsRef, "isValid", () => {});
    const errorCount = _isValid();

    if (errorCount > 0) {
      setToastMsg({
        msg: "toastMsgs:please_rectify_errors_with_count",
        locale_params: [{ key: "count", value: errorCount, isPlainText: true }],
      });
      setIsDropdownVisible(false);
      setAddButtonDropdownActiveId(null);
      return false;
    }

    return true;
  };

  const onVisibleChange = value => {
    setIsDropdownVisible(value);
    if (!isAddingQuestionsInBetween) return;
    if (value) {
      if (isQueriesV2Enabled) {
        let _sectionId = sectionItemId;
        if (isAddingAtStartOfMultipart) {
          _sectionId += "multipart";
        }
        setAddButtonDropdownActiveId(questionId ?? _sectionId);
      } else {
        //WORKSHEET_RESTRUCTURING:Remove this code
        let _sectionResourceId = sectionResourceId;
        if (isAddingAtStartOfMultipart) {
          _sectionResourceId += "multipart";
        }
        setAddButtonDropdownActiveId(questionResourceId ?? _sectionResourceId);
      }
    } else setAddButtonDropdownActiveId(null);
    if (!isAddDropdownButtonRevamp && value) {
      updateAddQuestionsInBetween({
        isAddingQuestionsInBetween: false,
        isAddingAtStartOfMultipart: false,
        questionResourceId: null,
        parentResourceId: null,
        nextQuestionResourceId: null,
        questionId: null,
        parentItemId: null,
        nextQuestionId: null,
      });
    }
  };

  const onDropdownOptionClick = params => {
    if (!isValid()) return;

    const { key = "" } = params || {};

    setIsDropdownVisible(false);
    setAddButtonDropdownActiveId(null);

    // If worksheet mutations v2 is enabled, uses the last section item id otherwise, uses the parent resource ID.
    const sectionId = isMutationsV2Enabled
      ? sectionItemId ?? parentItemId
      : sectionResourceId ?? parentResourceId;

    switch (key) {
      case ADD_QUESTION_FROM_LIBRARY:
        // open question library modal
        toggleQuestionLibraryModal();
        updateActiveQuestionId(ADD_QUESTION_FROM_LIBRARY);
        break;
      case GENERATE_USING_AI:
        handleAiQuestionGenerator(true);
        updateActiveQuestionId(GENERATE_USING_AI);

        // * Close the sidebar, when generating question with AI
        updateWorksheetSidebar({ isOpen: false, showErrors: false });
        break;
      case EXTRACT_FROM_DOC:
        updateActiveQuestionId(EXTRACT_FROM_DOC);

        // * Close the sidebar, when extracting question from doc
        updateWorksheetSidebar({ isOpen: false, showErrors: false });
        break;
      default:
        // create the question type
        onOptionClick({
          ...params,
          sectionId,
          putAfterItemId: isAddingQuestionsInBetween
            ? isMutationsV2Enabled
              ? questionId
              : questionResourceId
            : putAfterItemId,
        });
        break;
    }
  };

  const onAddSectionClick = () => {
    if (!isValid()) return;

    setIsDropdownVisible(false);
    setAddButtonDropdownActiveId(null);
    addSection();
  };

  const toggleQuestionLibraryModal = () => {
    setShowQuestionLibraryModal(prev => !prev);
    updateActiveQuestionId(null);
  };

  const getQuestionTypeItem = ({
    key,
    icon,
    label,
    onClick,
    onKeyDown,
    isQuestionCreationMethod = false,
  }) => {
    const creationMethodsWithAI =
      isQuestionCreationMethod && isWorksheetAIEnabled;

    const questionTypeClasses = classNames(
      classes.questionTypeItem,
      "text-body-1",
      {
        [classes.questionCreationMethodItem]: isQuestionCreationMethod,
        [classes.questionCreationMethodItemWithAI]:
          isQuestionCreationMethod && creationMethodsWithAI,
      }
    );

    const questionTypeItemLabelClasses = classNames(
      classes.questionTypeItemLabel,
      {
        "text-body-l": true,
        "text-body": isQuestionCreationMethod && creationMethodsWithAI,
      }
    );

    return (
      <div
        key={key}
        tabIndex={0}
        className={questionTypeClasses}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        aria-label={label}
        data-test-id={`worksheet-addQuestionsDropdownV2-questionTypeItem-${_.camelCase(
          label
        )}`}
      >
        <div className={classes.questionTypeItemIcon}>{icon}</div>
        <div className={questionTypeItemLabelClasses}>{label}</div>
      </div>
    );
  };

  const questionTypeTitleClasses = classNames(
    classes.questionTypeTitleV2,
    "text-label-l"
  );

  const textClasses = classNames("text-body-1", classes.text);
  const containerClasses = classNames({
    [classes.container]: !isRevampedSinglePageUI,
    [classes.paddingTop]: isButtonRounded && !isRevampedSinglePageUI,
    [classes.fullWidthContainer]: isFullWidthBtn && !isRevampedSinglePageUI,
    [classes.containerAddInBetween]: isAddDropdownButtonRevamp,
  });
  const NAVIGATION_KEYS = ["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"];

  const handleOptionSelect = option => {
    option.click();
    setIsDropdownVisible(false); // Close the dropdown after selection
    setAddButtonDropdownActiveId(null);
    setActiveFocusOptionIndex(-1); // Reset the active focus index
  };

  const dropdownKeyNavigation = e => {
    if (!NAVIGATION_KEYS.includes(e.key)) return;

    if (e.key === "Escape" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setAddButtonDropdownActiveId(null);
      setIsDropdownVisible(false);
    }

    // Handle arrow key navigation and option selection
    setActiveFocusOptionIndex(prevIndex => {
      if (e.key === "ArrowDown") {
        // Move focus to the next option (wrap around if at the last one)
        return (prevIndex + 1) % focusableOptions.length;
      } else if (e.key === "ArrowUp") {
        // Move focus to the previous option (wrap around if at the first one)
        return (
          (prevIndex - 1 + focusableOptions.length) % focusableOptions.length
        );
      } else if (e.key === "Enter") {
        // Select the currently focused option
        handleOptionSelect(focusableOptions[prevIndex]);
        return prevIndex; // Keep the focus on the selected option
      }
      return prevIndex;
    });
  };

  const questionTypesWrapperClasses = classNames(
    classes.questionTypesWrapper,
    classes.addSectionInstructionsWrapper
  );

  const addSectionLabel = t("common:add_new_with_label", {
    label: t("common:section"),
  });

  const disableAddingQuestions =
    isGeneratingAIQuestions ||
    isAIAssessmentToolsOpen ||
    isAIQuestionAssistantOpen;

  return (
    <>
      <div className={containerClasses}>
        <Dropdown
          visible={isDropdownVisible}
          overlay={
            <div
              id={WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_DROPDOWN_ID}
              className={classes.questionTypesContainer}
              ref={dropdownRef}
              onKeyDown={dropdownKeyNavigation}
              role="menu"
              tabIndex={-1}
            >
              <div className={questionTypesWrapperClasses}>
                {showAddInstructions
                  ? getQuestionTypeItem({
                      key: _.get(contentOption, "key"),
                      icon: <PageSquareOutlined />,
                      label: t("common:add_with_label", {
                        label: t("classRoom:instructions"),
                      }),
                      onClick: () => onDropdownOptionClick(contentOption),
                      onKeyDown: dropdownKeyNavigation,
                    })
                  : showAddSection &&
                    getQuestionTypeItem({
                      key: "section",
                      icon: <AddCircleOutlined />,
                      label: addSectionLabel,
                      onClick: onAddSectionClick,
                      onKeyDown: dropdownKeyNavigation,
                    })}
                {showAddInlineText &&
                  !isSubQuestion &&
                  getQuestionTypeItem({
                    key: _.get(contentOption, "key"),
                    icon: <PageSquareOutlined />,
                    label: t("common:add_with_label", {
                      label: t("worksheet:inline_text"),
                    }),
                    onClick: () => onDropdownOptionClick(contentOption),
                    onKeyDown: dropdownKeyNavigation,
                  })}
                {showAddSeparator &&
                  !isSubQuestion &&
                  getQuestionTypeItem({
                    key: _.get(separatorOption, "key"),
                    icon: <AddCircleOutlined />,
                    label: t("common:add_with_label", {
                      label: t("worksheet:separator"),
                    }),
                    onClick: () => onDropdownOptionClick(separatorOption),
                    onKeyDown: dropdownKeyNavigation,
                  })}
              </div>
              <div className={questionTypeTitleClasses}>
                {t("common:create_with_label", {
                  label: t("worksheet:questions"),
                })}
              </div>

              {_.map(groupedOptions, (options, index) => {
                const isLast = _.get(groupedOptions, "length") - 1 === index;
                const isQuestionCreationMethod = _.some(options, {
                  isQuestionCreationMethod: true,
                });
                const questionTypesWrapperClasses = classNames(
                  classes.questionTypesWrapper,
                  {
                    [classes.questionTypesWrapperV2]: isQuestionCreationMethod,
                    [classes.questionTypesWrapperV3]: !isQuestionCreationMethod,
                  }
                );
                const orSeparatorTextClasses = classNames(
                  classes.orSeparatorText,
                  "text-body"
                );

                return (
                  <Fragment key={index}>
                    <div className={questionTypesWrapperClasses}>
                      {_.map(options, option =>
                        getQuestionTypeItem({
                          key: option.key,
                          icon: option.icon,
                          label: option.label,
                          onClick: () => onDropdownOptionClick(option),
                          isQuestionCreationMethod:
                            option.isQuestionCreationMethod,
                          onKeyDown: dropdownKeyNavigation,
                        })
                      )}
                    </div>
                    {isLast ? null : (
                      <div className={classes.orSeparator}>
                        <div className={orSeparatorTextClasses}>
                          <span>{t("common:or_lowercase")}</span>
                          <span>{t("worksheet:create_from_scratch")}</span>
                        </div>
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          }
          onVisibleChange={onVisibleChange}
          testId="worksheet-addQuestionsDropdownV2-questionTypes"
          aria-haspopup="menu"
          aria-expanded={isDropdownVisible}
        >
          {isRevampedSinglePageUI ? (
            btnComponent
          ) : isButtonRounded ? (
            <div className={classes.roundedButton}>
              <IconButton
                icon={<AddOutlined />}
                _legacy_variant="plain"
                _legacy_size="x-small"
                variant="primary"
                type="plain"
                size="x-small"
                dsVersion="2.0"
                disabled={disableAddingQuestions}
                testId="worksheet-addQuestionsDropdown-multipart"
              />
            </div>
          ) : (
            <div className={classes.addQuestionTypeIcon}>
              <Button
                icon={<AddOutlined className={classes.iconPrimary} />}
                variant={"neutral"}
                type={"outlined"}
                size={isLargeButton ? "medium" : "small"}
                _legacy_size={isLargeButton ? "small" : "x-small"}
                _legacy_variant={"outlined-subtle"}
                isFullWidth={isFullWidthBtn}
                disabled={disableAddingQuestions}
                dsVersion={"2.0"}
                className={classes.addQuestionButtonRevamp}
                testId="worksheet-addQuestionsDropdown-sidebar"
              >
                {t("common:add_with_label", {
                  label: `${t("worksheet:questions")}/${t("common:section")}`,
                })}
              </Button>
            </div>
          )}
        </Dropdown>
        {!_.isEmpty(labelLocale) ? (
          <span className={textClasses}>{t(labelLocale)}</span>
        ) : null}
        {isAddDropdownButtonRevamp && (
          <Button
            onClick={() => onDropdownOptionClick(contentOption)}
            aria-label="Add content box"
            variant="primary"
            type="inline"
            dsVersion="2.0"
            isFullWidth={true}
            className={classes.addInBetweenLineButton}
            testId="worksheet-addQuestionsDropdown-questionTypeItem-inlineText"
          >
            <hr className={classes.addInBetweenHr} />
          </Button>
        )}
      </div>
      {showQuestionLibraryModal ? (
        <QuestionLibraryModal
          toggleQuestionLibraryModal={toggleQuestionLibraryModal}
          isSubQuestion={isSubQuestion}
          parentResourceId={parentResourceId}
          parentItemId={parentItemId}
          putAfterItemId={putAfterItemId}
        />
      ) : null}
    </>
  );
});

const mapActionCreators = {
  setToastMsg,
  updateAddQuestionsInBetween,
};

const mapStateToProps = (state, ownProps) => {
  const { activeQuestionId } = ownProps;

  const curriculumType = getCurrentCurriculumProgramType(state);
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const isAddingQuestionsInBetween = _.get(
    state,
    "worksheet.addQuestionsInBetween.isAddingQuestionsInBetween",
    false
  );

  const questionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionResourceId",
    null
  );

  const sectionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentResourceId",
    null
  );

  const isAddingAtStartOfMultipart = _.get(
    state,
    "worksheet.addQuestionsInBetween.isAddingAtStartOfMultipart",
    false
  );

  const isAIAssessmentToolsOpen = _.get(
    state,
    "worksheet.aiAssessmentTools.isOpen",
    false
  );

  const visibleQuestionAssistantIds = _.get(
    state,
    "worksheet.visibleQuestionAssistantIds",
    []
  );
  const questionId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionId",
    null
  );

  const sectionItemId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentItemId",
    null
  );

  const isAIQuestionAssistantOpen = _.includes(
    visibleQuestionAssistantIds,
    activeQuestionId
  );

  return {
    curriculumType,
    isGeneratingAIQuestions,
    isAddingQuestionsInBetween,
    questionResourceId,
    sectionResourceId,
    isAddingAtStartOfMultipart,
    isAIAssessmentToolsOpen,
    isAIQuestionAssistantOpen,
    questionId,
    sectionItemId,
  };
};

AddQuestionsDropdownV2.propTypes = {
  onOptionClick: PropTypes.func,
  isSubQuestion: PropTypes.bool,
  sectionId: PropTypes.string,
  putAfterItemId: PropTypes.string,
  addSection: PropTypes.func,
  showAddInstructions: PropTypes.bool,
  updateActiveQuestionId: PropTypes.func,
  handleAiQuestionGenerator: PropTypes.func,
  isButtonRounded: PropTypes.bool,
  labelLocale: PropTypes.string,
  isFullWidthBtn: PropTypes.bool,
  isLargeButton: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  isToddleLibraryPortal: PropTypes.bool,
  isRevampedSinglePageUI: PropTypes.bool,
  isAddDropdownButtonRevamp: PropTypes.bool,
  showAddSection: PropTypes.bool,
  showAddSeparator: PropTypes.bool,
  showAddInlineText: PropTypes.bool,
  setAddButtonDropdownActiveId: PropTypes.func,
  isQueriesV2Enabled: PropTypes.bool,
};

AddQuestionsDropdownV2.defaultProps = {
  onOptionClick: _.noop,
  isSubQuestion: false,
  addSection: _.noop,
  showAddInstructions: false,
  updateActiveQuestionId: _.noop,
  handleAiQuestionGenerator: _.noop,
  isButtonRounded: false,
  labelLocale: "",
  isFullWidthBtn: false,
  isLargeButton: false,
  isFromInternalTool: false,
  isToddleLibraryPortal: false,
  isRevampedSinglePageUI: false,
  isAddDropdownButtonRevamp: false,
  showAddSection: true,
  showAddSeparator: false,
  showAddInlineText: false,
  setAddButtonDropdownActiveId: _.noop,
  isQueriesV2Enabled: false,
};

AddQuestionsDropdownV2.displayName = "AddQuestionsDropdownV2";

export default connect(
  mapStateToProps,
  mapActionCreators
)(AddQuestionsDropdownV2);
